import { ChatHttpError, streamChat } from "../../../chat/services/api";
import type { RefreshTokenFn } from "../../../shared/api/fetchWithRefresh";
import { graphGetJson } from "../../email/services/graph";
import type { EmailContext } from "../../email/types";
import { agendaFromReply, LIST_ITEM_RE, withoutTeamsBlock } from "../domain/agenda";

/*
 * "Draft With AiVA": a real AI agenda from the existing chat endpoint (the
 * same /api/v2/chat the Chat tab uses), without switching to Chat.
 *
 * The calendar_assistant skill has two agenda paths. Named an existing meeting,
 * it looks the meeting up and saves the agenda into the invite itself. Asked for
 * an agenda "from this thread", its own rules say to stream the agenda and NOT
 * save it. The pane uses the second path, with the meeting's email thread as the
 * "open email", so the agenda comes back unsaved for the user to edit and Save.
 */

/** Keeps these turns in their own server-side thread bucket, so Chat never opens one as the user's conversation. */
const AGENDA_CONTEXT_ID = "aiva-calendar-agenda-draft";
/** As much of the email body as Chat itself sends. The Teams join details are cut first: the agenda and brief
 * don't need them, and the backend's secret screen reads the meeting passcode as a password. */
const BODY_CAP = 6000;

/** A subject without its RE: / FW: prefixes. */
export function withoutPrefixes(subject: string): string {
  let s = subject.trim();
  for (;;) {
    const next = s.replace(/^(?:re|fw|fwd|aw|wg)\s*:\s*/i, "");
    if (next === s) return s;
    s = next;
  }
}

interface Recipient {
  readonly emailAddress?: { readonly address?: string | null } | null;
}

interface MessagesResponse {
  readonly value?: readonly {
    readonly id?: string;
    readonly subject?: string | null;
    readonly from?: Recipient | null;
    readonly toRecipients?: readonly Recipient[] | null;
    readonly ccRecipients?: readonly Recipient[] | null;
    readonly body?: { readonly content?: string | null } | null;
    readonly conversationId?: string | null;
    readonly receivedDateTime?: string | null;
    readonly internetMessageId?: string | null;
    readonly importance?: string | null;
  }[];
}

const address = (r: Recipient | null | undefined) => (r?.emailAddress?.address ?? "").trim();

/**
 * The latest email in your mailbox with the meeting's subject (RE:/FW: aside),
 * as the "open email" the agenda is drafted from. Null when there is none.
 */
export async function findMeetingEmail(
  token: string,
  refresh: RefreshTokenFn | undefined,
  subject: string,
): Promise<EmailContext | null> {
  const wanted = withoutPrefixes(subject);
  const query = wanted.replace(/["\\]/g, " ").trim();
  if (!query) return null;
  const search = encodeURIComponent(`"subject:${query}"`);
  const result = await graphGetJson<MessagesResponse>(
    `/me/messages?$search=${search}&$top=10` +
      "&$select=id,subject,from,toRecipients,ccRecipients,body,conversationId,receivedDateTime,internetMessageId,importance",
    token,
    refresh,
    { Prefer: 'outlook.body-content-type="text"' },
  );
  const latest = (result.value ?? [])
    .filter((m) => withoutPrefixes(m.subject ?? "").toLowerCase() === wanted.toLowerCase())
    .sort((a, b) => Date.parse(b.receivedDateTime ?? "") - Date.parse(a.receivedDateTime ?? ""))[0];
  if (!latest) return null;
  return {
    subject: latest.subject ?? wanted,
    normalizedSubject: wanted,
    from: address(latest.from),
    sender: address(latest.from),
    to: (latest.toRecipients ?? []).map(address).filter(Boolean),
    cc: (latest.ccRecipients ?? []).map(address).filter(Boolean),
    body: withoutTeamsBlock(latest.body?.content ?? "").slice(0, BODY_CAP),
    dateReceived: latest.receivedDateTime ?? "",
    dateModified: latest.receivedDateTime ?? "",
    importance: latest.importance ?? "normal",
    attachments: [],
    itemId: latest.id ?? null,
    conversationId: latest.conversationId ?? null,
    internetMessageId: latest.internetMessageId ?? null,
    itemType: "message",
  };
}

/** The invite itself as the "open email", when no email thread was found. */
export function inviteAsEmail(meeting: {
  readonly subject: string;
  readonly organizerEmail: string;
  readonly attendees: readonly string[];
  readonly inviteText: string;
}): EmailContext {
  const subject = meeting.subject || "Meeting";
  return {
    subject,
    normalizedSubject: withoutPrefixes(subject),
    from: meeting.organizerEmail,
    sender: meeting.organizerEmail,
    to: [...meeting.attendees],
    cc: [],
    body: withoutTeamsBlock(meeting.inviteText).slice(0, BODY_CAP),
    dateReceived: "",
    dateModified: "",
    importance: "normal",
    attachments: [],
    itemId: null,
    conversationId: null,
    internetMessageId: null,
    itemType: "appointment",
  };
}

export interface AgendaDraft {
  /** The agenda as plain lines for the Agenda box. */
  readonly text: string;
  /** The AI saved it to the invite anyway (it shouldn't on this path; reported so the screen can say so). */
  readonly savedByAiva: boolean;
}

/** Why a request failed, in words for the screen. `what` is e.g. "draft the agenda". */
function failureMessage(err: unknown, what: string): string {
  if (err instanceof ChatHttpError) {
    if (err.status === 401) return "Your AiVA sign-in has expired. Reopen the add-in and try again.";
    if (err.status === 403) return `Your AiVA access doesn't let it ${what}.`;
    if (err.status === 429 || err.status >= 500) return "AiVA is busy right now. Try again in a moment.";
  }
  return `AiVA couldn't ${what}. Try again.`;
}

/**
 * One hidden turn to the calendar assistant through the existing chat
 * endpoint, with `email` as the open email. `onReply` gets the reply so far as
 * it streams in. Throws an Error whose message can be shown as it is.
 */
export async function askCalendarAssistant(options: {
  readonly apiToken: string;
  readonly refresh: RefreshTokenFn;
  readonly email: EmailContext;
  readonly message: string;
  readonly userDisplayName: string;
  readonly signal: AbortSignal;
  /** For error messages: "draft the agenda", "write the brief". */
  readonly what: string;
  readonly onReply?: (reply: string) => void;
}): Promise<{ readonly reply: string; readonly savedByAiva: boolean }> {
  let reply = "";
  let savedByAiva = false;
  let refused: string | null = null;
  try {
    await streamChat(
      options.message,
      [],
      null,
      "outlook",
      options.apiToken,
      (ev) => {
        if (ev.event === "token" && ev.data.content) {
          reply += ev.data.content;
          options.onReply?.(reply);
        } else if ((ev.event === "tool_start" || ev.event === "tool_end") && ev.data.tool === "produce_agenda") {
          savedByAiva = true;
        } else if (ev.event === "error" || ev.event === "guardrail_flagged") {
          refused = ev.data.message ?? `AiVA couldn't ${options.what}.`;
        } else if (ev.event === "paused" || ev.event === "approval_required") {
          refused = `AiVA needs more from you to ${options.what}. Ask it in Chat instead.`;
        }
      },
      {
        forcedSkillKey: "calendar_assistant",
        emailContext: options.email,
        surface: "read",
        hiddenTurn: true,
        contextId: AGENDA_CONTEXT_ID,
        userDisplayName: options.userDisplayName || null,
        signal: options.signal,
        refresh: options.refresh,
      },
    );
  } catch (err: unknown) {
    if (options.signal.aborted) throw err;
    throw new Error(failureMessage(err, options.what));
  }
  if (refused) throw new Error(refused);
  return { reply, savedByAiva };
}

/** How much of the invite's text and the user's own notes go into the request. */
const PROMPT_TEXT_CAP = 1500;

/** The meeting the agenda is for, as the AI is told about it. */
export interface AgendaMeeting {
  readonly subject: string;
  readonly durationLabel: string;
  readonly location: string;
  readonly organizerName: string;
  readonly attendeeNames: readonly string[];
  /** The invite's own text (without the Teams join details). */
  readonly inviteText: string;
  /** Whatever is already in the Agenda box, to build on. */
  readonly notes: string;
}

/**
 * The request: everything known about the meeting, and the four sections to
 * fill. A thin email thread alone gave back bare headings, so the meeting's own
 * details and the user's notes always go with it. The date and time are left
 * out, and the request says not to touch any meeting, so the skill stays on
 * its draft-only "from this thread" path and doesn't look the meeting up and
 * save the agenda itself.
 *
 * `fromThread` is false when the invite stands in for a thread that wasn't
 * found. The skill is told to fetch the thread when the open email is thin, but
 * the invite has no conversation id: the id it makes up is a request Microsoft
 * 365 rejects, and that error ends the turn with no agenda. So that request
 * says there is no thread to fetch.
 */
function agendaRequest(meeting: AgendaMeeting, fromThread: boolean): string {
  const quote = (text: string) => `"${text.trim().slice(0, PROMPT_TEXT_CAP).replace(/\s+/g, " ")}"`;
  const lines = [
    fromThread
      ? `Draft an agenda from this thread for the "${meeting.subject}" meeting.`
      : `Draft an agenda for this "${meeting.subject}" meeting invite.`,
    `Meeting details: ${meeting.durationLabel}` +
      (meeting.location ? `, ${meeting.location}` : "") +
      (meeting.organizerName ? `, organised by ${meeting.organizerName}` : "") +
      (meeting.attendeeNames.length > 0 ? `, with ${meeting.attendeeNames.join(", ")}` : "") +
      ".",
  ];
  if (meeting.inviteText.trim()) lines.push(`The invite says: ${quote(meeting.inviteText)}.`);
  if (meeting.notes.trim()) lines.push(`My notes so far, to build on: ${quote(meeting.notes)}.`);
  if (!fromThread) {
    lines.push(
      "This invite has no email thread: don't call fetch_conversation_thread or search the mailbox. " +
        "Write the agenda from the meeting details here.",
    );
  }
  lines.push(
    "Use exactly these four headings: Purpose, Key points, Open questions, Action items.",
    "Under each heading write one to four specific items drawn from the thread, the meeting details and my notes, " +
      "and name who owns each action where you can. Never leave a heading empty: where the thread doesn't cover it, " +
      "write what this meeting needs to settle there, based on its subject and the people in it.",
    "This is a draft for me to review: don't look up or change any meeting, just write the agenda.",
    'Reply with the agenda only, as plain text: each heading on its own line, then each item on its own line starting with "- ".',
  );
  return lines.join("\n");
}

/** The headings `agendaRequest` asks for, lowercased. */
const AGENDA_HEADINGS = new Set(["purpose", "key points", "open questions", "action items"]);

/** With too little to go on, the skill answers with a sentence about what's missing; that sentence isn't an agenda. */
const NOT_ENOUGH_TO_DRAFT =
  "AiVA didn't have enough to draft an agenda from. Type a line or two about what the meeting is for, then Redraft.";

/** An agenda has a list item or one of the requested headings. A sentence about what's missing has neither. */
function looksLikeAgenda(text: string): boolean {
  return text
    .split("\n")
    .some((line) => LIST_ITEM_RE.test(line) || AGENDA_HEADINGS.has(line.trim().replace(/:$/, "").toLowerCase()));
}

/**
 * Ask AiVA for an agenda from `email`. `onText` gets the agenda so far as it
 * streams in. Throws an Error whose message can be shown as it is.
 */
export async function draftAgenda(options: {
  readonly apiToken: string;
  readonly refresh: RefreshTokenFn;
  readonly email: EmailContext;
  readonly meeting: AgendaMeeting;
  readonly userDisplayName: string;
  readonly signal: AbortSignal;
  readonly onText: (text: string) => void;
}): Promise<AgendaDraft> {
  const { reply, savedByAiva } = await askCalendarAssistant({
    apiToken: options.apiToken,
    refresh: options.refresh,
    email: options.email,
    message: agendaRequest(options.meeting, Boolean(options.email.conversationId)),
    userDisplayName: options.userDisplayName,
    signal: options.signal,
    what: "draft the agenda",
    onReply: (raw) => options.onText(agendaFromReply(raw)),
  });
  const text = agendaFromReply(reply);
  if (!text) throw new Error("AiVA didn't return an agenda. Try again, or write it yourself.");
  if (!looksLikeAgenda(text)) throw new Error(NOT_ENOUGH_TO_DRAFT);
  return { text, savedByAiva };
}
